import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, RangePicker, Select, Button, message, Alert } from "../../../Components/UI";
import { useDispatch, useSelector } from "react-redux";
import { GetTeacherList, calculateTeacherScores } from "../../../redux/userSlice";
import { SyncOutlined } from "../../../Components/UI/Icons";
import { Box, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { getUserRoleTag } from "../../../Utils/auth";

const SafeRangePicker = RangePicker || DatePicker?.RangePicker;

function Step1_BasicDetails({ form, formValues, setFormValues, id }) {
  const dispatch = useDispatch();
  const { GetTeachersLists } = useSelector((s) => s?.user);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    dispatch(GetTeacherList());
  }, [dispatch]);

  const teachersArray = Array.isArray(GetTeachersLists) 
    ? GetTeachersLists 
    : GetTeachersLists?.data || [];

  const teacherOptions = teachersArray
    .filter((t) => t?.isActive !== false)
    .map((t) => ({
      label: `${t.name} [${getUserRoleTag(t)}]`,
      value: t._id,
    }));

  const handleCalculate = async () => {
    const fromDate = form.getFieldValue("fromDate");
    const toDate = form.getFieldValue("toDate");
    const teachers = form.getFieldValue("teachers");

    if (!fromDate || !toDate || !teachers || teachers.length === 0) {
      message.warning("Please select Date Range and at least one Teacher to sync data.");
      return;
    }

    setCalculating(true);
    try {
      const res = await dispatch(
        calculateTeacherScores({
          teacherIds: teachers,
          fromDate: fromDate?.toISOString ? fromDate.toISOString() : fromDate,
          toDate: toDate?.toISOString ? toDate.toISOString() : toDate,
        })
      ).unwrap();

      if (res?.success) {
        // Merge the newly fetched scores with any existing manually entered scores
        const existingScores = (formValues.teacherScores || []).filter(Boolean);
        const newScores = res.data.map((calculated) => {
          // Find if we already have manual data for this teacher (guard against null slots)
          const existing = existingScores.find((s) => s && s.teacherId === calculated.teacherId) || {};
          return {
            ...existing,
            ...calculated, // Overwrite auto-calculated fields
            teacherName:
              (teacherOptions || []).find((t) => t.value === calculated.teacherId)?.label ||
              "Unknown",
          };
        });

        const newFormValues = { ...formValues, teacherScores: newScores };
        setFormValues(newFormValues);
        form.setFieldsValue({ teacherScores: newScores });

        // Save to local storage
        if (id) {
          const currentFormValues = { ...form.getFieldsValue(true), teacherScores: newScores };
          localStorage.setItem(`accountability_form_${id}`, JSON.stringify(currentFormValues));
        }

        message.success("Successfully synced data from existing forms.");
      }
    } catch (error) {
      console.error("Sync error:", error);
      message.error(typeof error === "string" ? error : "Failed to calculate teacher scores.");
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Box>
      <Alert
        message="Basic Details"
        description="Select the date range and teachers to evaluate. Once selected, click 'Sync Teacher Data' to automatically fetch their Classroom Walkthrough and Notebook Checking scores."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form.Item
        name="formName"
        label="Report Name"
        rules={[{ required: true, message: "Please enter a report name" }]}
      >
        <Input placeholder="e.g. October 2023 Accountability Report" size="large" />
      </Form.Item>

      <Box display="flex" gap={4} mb={6}>
        <Form.Item
          name="fromDate"
          label="From Date"
          rules={[{ required: true, message: "Please select start date" }]}
          style={{ flex: 1 }}
          getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
        >
          <DatePicker size="large" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="toDate"
          label="To Date"
          rules={[{ required: true, message: "Please select end date" }]}
          style={{ flex: 1 }}
          getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
        >
          <DatePicker size="large" style={{ width: "100%" }} />
        </Form.Item>
      </Box>

      <Form.Item
        name="teachers"
        label="Select Teachers"
        rules={[{ required: true, message: "Please select at least one teacher" }]}
      >
        <Select
          mode="multiple"
          showSearch
          placeholder="Search and select teachers..."
          options={teacherOptions}
          size="large"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>

      <Button
        type="dashed"
        icon={<SyncOutlined spin={calculating} />}
        onClick={handleCalculate}
        loading={calculating}
        block
        size="large"
      >
        Sync Teacher Data (Walkthrough & Notebook)
      </Button>
    </Box>
  );
}

export default Step1_BasicDetails;
